import { ACCESS_SEARCH_ROWS } from "./rows/access";
import { APPEARANCE_SEARCH_ROWS } from "./rows/appearance";
import { PREFERENCES_SEARCH_ROWS } from "./rows/preferences";
import { WORKSPACE_SEARCH_ROWS } from "./rows/workspace";
import type {
  SettingsSearchPage,
  SettingsSearchResult,
  SettingsSearchRow,
} from "./settings-search-types";

export type {
  SettingsSearchPage,
  SettingsSearchResult,
  SettingsSearchRow,
} from "./settings-search-types";
export {
  requestSettingsHighlight,
  SettingsHighlightTarget,
  useSettingsHighlight,
} from "./settings-highlight";

/**
 * Row-level search coverage, keyed by the sidebar route. A page whose rows
 * aren't catalogued yet still matches by its page name — list it here with
 * `[]` so the coverage test names it as owed rather than forgotten. Each
 * page's rows live in `rows/<page>.ts` beside the anchor ids the page itself
 * imports.
 */
export const SETTINGS_SEARCH_ROWS: Record<string, readonly SettingsSearchRow[]> = {
  "/settings/preferences": PREFERENCES_SEARCH_ROWS,
  "/settings/appearance": APPEARANCE_SEARCH_ROWS,
  "/settings/profile": [],
  "/settings/access": ACCESS_SEARCH_ROWS,
  "/settings/issue-types": [],
  "/settings/issue-sources": [],
  "/settings/outcomes": [],
  "/settings/trash": [],
  "/settings/workspace": WORKSPACE_SEARCH_ROWS,
  "/settings/members": [],
  "/settings/teams": [],
  "/settings/permissions": [],
  "/settings/workflows": [],
  "/settings/api": [],
};

function rowMatches(row: SettingsSearchRow, q: string): boolean {
  return (
    row.label.toLowerCase().includes(q) ||
    (row.keywords?.some((k) => k.toLowerCase().includes(q)) ?? false)
  );
}

/**
 * The sidemenu search (the keyboard-shortcuts pattern): every reachable page
 * that contains the query — in its own name or one of its catalogued rows —
 * with the matching rows as jump targets beneath. Permissions gate twice:
 * `pages` is the sidebar's already-filtered list (a hidden page hides its
 * rows with it), and a row carrying its own `permKey` — a section gated
 * finer than its page — is checked against `perms`, the sidebar's
 * pre-resolved permission map. Case-insensitive substring, nav order.
 */
export function searchSettingsNav(
  query: string,
  pages: readonly SettingsSearchPage[],
  perms: Readonly<Record<string, boolean>>,
): SettingsSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results: SettingsSearchResult[] = [];
  for (const page of pages) {
    const pageHit = page.label.toLowerCase().includes(q);
    const rows = (SETTINGS_SEARCH_ROWS[page.key] ?? []).filter(
      (row) =>
        (!row.permKey || (perms[row.permKey] ?? false)) && rowMatches(row, q),
    );
    if (pageHit || rows.length > 0) results.push({ page, rows });
  }
  return results;
}
